import { CommonModule, DatePipe } from "@angular/common";
import { Component, inject, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { OrderTicket } from "@models/order";
import { UserInfo } from "@models/user";
import { cpfValidator, nameValidator } from "@shared/validatos";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { finalize, Subject, takeUntil } from "rxjs";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { DdiSelectComponent } from "../../../../../shared/components/ddi-select/ddi-select.component";
import { DialogHeaderComponent } from "../../../../../shared/components/dialog-header/dialog-header.component";
import { UtilsService } from "../../../../../utils/utils.service";
import { SnackBarService } from "../../../../services/snack-bar.service";
import {
  RegisterTicketOwnerRequest,
  TicketManagementService,
} from "../../../../services/ticket-management.service";

export interface TicketAssignDialogData {
  ticket: OrderTicket;
  currentUser: UserInfo;
}

@Component({
  selector: "app-ticket-assign-dialog",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    DdiSelectComponent,
    NgxMaskDirective,
    MatDialogModule,
    DialogHeaderComponent,
  ],
  templateUrl: "./ticket-assign-dialog.component.html",
  styleUrl: "./ticket-assign-dialog.component.scss",
  providers: [provideNgxMask(), DatePipe],
})
export class TicketAssignDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<TicketAssignDialogComponent>);

  private readonly snackbarService = inject(SnackBarService);
  private readonly utilsService = inject(UtilsService);
  private readonly ticketService = inject(TicketManagementService);
  private readonly fb = inject(FormBuilder);

  private destroy$ = new Subject<void>();

  // Dialog data
  ticket: OrderTicket;
  currentUser: UserInfo;

  constructor(@Inject(MAT_DIALOG_DATA) public data: TicketAssignDialogData) {
    this.ticket = data.ticket;
    this.currentUser = data.currentUser;
  }

  isLoading = false;

  assignForm = this.fb.group({
    isForeigner: true,
    user_email: ["", [Validators.required, Validators.email]],
    user_name: ["", [Validators.required, nameValidator]],
    user_cpf: ["", [Validators.required, cpfValidator]],
    user_identity_card: ["", [Validators.required]],
  });

  ngOnInit(): void {
    this.subscribeToIsForeignerChange();
    this.preencherFormulario();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  private subscribeToIsForeignerChange() {
    const { isForeigner, user_cpf, user_identity_card } =
      this.assignForm.controls;

    isForeigner.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((isForeignerValue) => {
        if (isForeignerValue) {
          user_cpf.removeValidators(Validators.required);
          user_cpf.disable();
          user_identity_card.addValidators(Validators.required);
          user_identity_card.enable();
        } else {
          user_identity_card.clearValidators();
          user_identity_card.disable();
          user_cpf.addValidators(Validators.required);
          user_cpf.enable();
        }
        user_cpf.updateValueAndValidity({ emitEvent: false });
        user_identity_card.updateValueAndValidity({ emitEvent: false });
        this.assignForm.updateValueAndValidity();
      });
  }

  private preencherFormulario() {
    this.assignForm.patchValue({
      isForeigner: this.ticket.user_cpf
        ? !this.ticket.user_cpf
        : !this.currentUser.cpf,
      user_email: this.ticket.user_email
        ? this.ticket.user_email
        : this.currentUser.email,
      user_name: this.ticket.user_name
        ? `${this.ticket.user_name} ${this.ticket.user_last_name}`
        : `${this.currentUser.name} ${this.currentUser.last_name}`,
      user_cpf: this.ticket.user_cpf
        ? this.ticket.user_cpf
        : this.currentUser.cpf,
      user_identity_card: this.ticket.user_identity_card
        ? this.ticket.user_identity_card
        : this.currentUser.identity_card,
    });
  }

  eventDateHasPassed() {
    return this.utilsService.isPast(this.ticket.event_end_date);
  }

  onSubmitAssign() {
    if (this.assignForm.invalid) {
      this.snackbarService.openSnackBar(
        "Formulário inválido!",
        "Fechar",
        "error",
      );
      return;
    }
    this.isLoading = true;

    const { user_email, user_cpf, user_name, user_identity_card, isForeigner } =
      this.assignForm.value;
    const nomeLimpo = user_name!
      .replace(/\s{2,}/g, " ")
      .trim()
      .split(" ");

    const firstName = nomeLimpo[0];
    const lastName = nomeLimpo?.slice(1)?.join(" ");

    const registerRequest: RegisterTicketOwnerRequest = {
      user_cpf: !isForeigner ? user_cpf : null,
      user_identity_card: isForeigner ? user_identity_card : null,
      user_email: user_email!,
      user_name: firstName,
      user_last_name: lastName,
    };

    this.ticketService
      .registerTicketOwner(this.ticket.id, registerRequest)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar(
            "Ticket cadastrado com sucesso!",
            "Fechar",
            "success",
          );
          this.dialogRef.close(true);
        },
        error: (error: Error) => {
          this.snackbarService.openSnackBar(error.message, "Fechar", "error");
        },
      });
  }
}
